/**
 * Wnioski o informację IFT-1 (Etap 52, ADR-0053).
 *
 * Art. 42 ust. 4 ustawy o PIT: na pisemny wniosek podatnika płatnik ma
 * czternaście dni od dnia ZŁOŻENIA wniosku na sporządzenie i przesłanie
 * imiennej informacji — to ten sam wzór co roczny IFT-1R, ale za rok do dnia
 * wniosku. Przepis chce postaci pisemnej, więc portal wniosku nie przyjmuje:
 * kadry zapisują ten, który wpłynął, a termin liczy się z daty złożenia.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { api, type IftRequestInput, apiYear } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface IftRequestsProps {
  employeeId: string
  /** Zapis wniosku i wystawienie informacji wymagają uprawnienia płacowego. */
  canEdit: boolean
  /**
   * Czy karta dotyczy rodzaju umowy osoby (fala 8, grupa 87; D98). Przy
   * „nie dotyczy" karta pokazuje tylko istniejące wpisy, do odczytu, a bez
   * wpisów nie renderuje się wcale.
   */
  applicable?: boolean
}

function currentYear(): number {
  return apiYear()
}

function emptyDraft(): Omit<IftRequestInput, 'employeeId'> {
  return { requestedOn: '', year: currentYear() }
}

export function IftRequests({ employeeId, canEdit: canEditProp, applicable = true }: IftRequestsProps) {
  const canEdit = canEditProp && applicable
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { requestedOn: useId(), year: useId(), note: useId() }

  const [draft, setDraft] = useState<Omit<IftRequestInput, 'employeeId'> | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const register = useQuery({
    queryKey: ['ift-requests', employeeId],
    queryFn: () => api.iftRequests(employeeId),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['ift-requests'] })
    void queryClient.invalidateQueries({ queryKey: ['employee-documents', employeeId] })
  }

  const record = useMutation({
    mutationFn: (values: Omit<IftRequestInput, 'employeeId'>) =>
      api.recordIftRequest({
        ...values,
        employeeId,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: (result) => {
      toast.show(t('iftRequests.saved', { date: formatDate(result.deadline, 'dot') }), {
        tone: 'success',
      })
      setDraft(null)
      setNote('')
      setError(null)
      refresh()
    },
    onError: () => setError('iftRequests.errorGeneric'),
  })

  const issue = useMutation({
    mutationFn: (requestId: string) => api.issueIftRequest(requestId),
    onSuccess: (result) => {
      /*
       * Wystawienie po terminie z art. 42 ust. 4 jest faktem, nie sukcesem —
       * mówimy o nim tonem ostrzeżenia, a rejestr zostawia je „po terminie".
       */
      toast.show(result.late ? t('iftRequests.issuedLate') : t('iftRequests.issued'), {
        tone: result.late ? 'danger' : 'success',
      })
      setError(null)
      refresh()
    },
    onError: () => setError('iftRequests.errorIssue'),
  })

  const items = register.data?.items ?? []
  const overdue = items.some((item) => item.state === 'po_terminie' && item.issuedOn === null)

  /* Karta „nie dotyczy” bez wpisów nie miga też w trakcie ładowania. */
  if (!applicable && items.length === 0) return null

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('iftRequests.title')}</h3>
        {canEdit && draft === null ? (
          <Button size="sm" variant="secondary" onClick={() => setDraft(emptyDraft())}>
            {t('iftRequests.add')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('iftRequests.lead')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {register.isPending ? (
        <Spinner label={t('iftRequests.loading')} />
      ) : items.length === 0 ? (
        <EmptyState title={t('iftRequests.empty')} description={t('iftRequests.emptyBody')} />
      ) : (
        <>
          <Card pad="flush" className={styles.list}>
            {items.map((item) => (
              <div key={item.id} className={styles.row}>
                <span className={styles.rowName}>{formatDate(item.requestedOn, 'dot')}</span>
                <span className={styles.rowMeta}>
                  {t('iftRequests.year', { year: item.year })}
                </span>
                <span className={styles.rowMeta}>
                  {t('iftRequests.deadline', { date: formatDate(item.deadline, 'dot') })}
                </span>
                <span className={styles.rowMeta}>
                  {item.issuedOn === null
                    ? ''
                    : t('iftRequests.issuedOn', { date: formatDate(item.issuedOn, 'dot') })}
                </span>
                <StatusPill
                  tone={
                    item.state === 'po_terminie'
                      ? 'danger'
                      : item.state === 'wystawiona'
                        ? 'success'
                        : 'neutral'
                  }
                >
                  {t(`iftRequests.state.${item.state}`)}
                </StatusPill>
                {canEdit && item.issuedOn === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={issue.isPending}
                    onClick={() => {
                      setError(null)
                      issue.mutate(item.id)
                    }}
                  >
                    {t('iftRequests.issue')}
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
          {overdue ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('iftRequests.overdueWarning')}
            </InfoBanner>
          ) : null}
        </>
      )}

      {draft !== null ? (
        <>
          <div className={styles.formGrid}>
            <FormField
              label={t('iftRequests.fields.requestedOn')}
              htmlFor={ids.requestedOn}
              hint={t('iftRequests.fields.requestedOnHint')}
            >
              <Input
                id={ids.requestedOn}
                type="date"
                value={draft.requestedOn}
                onChange={(event) => setDraft({ ...draft, requestedOn: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('iftRequests.fields.year')}
              htmlFor={ids.year}
              hint={t('iftRequests.fields.yearHint')}
            >
              <Input
                id={ids.year}
                value={String(draft.year)}
                onChange={(event) =>
                  setDraft({ ...draft, year: Number(event.target.value) || currentYear() })
                }
              />
            </FormField>
            <FormField label={t('iftRequests.fields.note')} htmlFor={ids.note}>
              <Input
                id={ids.note}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </FormField>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              {t('iftRequests.cancel')}
            </Button>
            <Button
              disabled={record.isPending || draft.requestedOn === ''}
              onClick={() => {
                setError(null)
                record.mutate(draft)
              }}
            >
              {record.isPending ? <Spinner label={t('iftRequests.saving')} /> : t('iftRequests.save')}
            </Button>
          </div>
        </>
      ) : null}
    </section>
  )
}
